import { findParentNodeClosestToPos } from "@tiptap/core";
import { NodeSelection, Selection, TextSelection } from "@tiptap/pm/state";

import { adjacentNodeInDirection, isBlockCollapsible, isCollapsed } from "./utils";

import type { Editor } from "@tiptap/core";
import type { Node as PMNode, ResolvedPos } from "@tiptap/pm/model";
import type { Selection as PMSelection } from "@tiptap/pm/state";
import type { EditorView } from "@tiptap/pm/view";

export function isCollapsedSelection(selection: PMSelection): selection is NodeSelection {
  return (
    selection instanceof NodeSelection &&
    isCollapsed(selection.node) &&
    isBlockCollapsible(selection.node)
  );
}

export function closestCollapsedBlock($pos: ResolvedPos): { node: PMNode; pos: number } | null {
  const found = findParentNodeClosestToPos(
    $pos,
    (node) => isCollapsed(node) && isBlockCollapsible(node),
  );

  return found ? { node: found.node, pos: found.pos } : null;
}

export function collapsedBlockSpannedBy(
  selection: PMSelection,
): { node: PMNode; pos: number } | null {
  const node = selection.$from.doc.nodeAt(selection.from);

  if (
    isCollapsed(node) &&
    isBlockCollapsible(node) &&
    selection.to === selection.from + node.nodeSize
  ) {
    return { node, pos: selection.from };
  }

  return null;
}

export function collapsedPositionsNear(view: EditorView, pos: number): number[] {
  const { doc } = view.state;
  const $pos = doc.resolve(pos);
  const { depth, parent } = $pos;
  const index = $pos.index(depth);

  const isCollapsedBlock = (i: number): boolean => {
    const child = parent.maybeChild(i);
    return isBlockCollapsible(child) && isCollapsed(child);
  };

  let first = index;
  while (first - 1 >= 0 && isCollapsedBlock(first - 1)) {
    first--;
  }

  let last = index;
  while (last + 1 < parent.childCount && isCollapsedBlock(last + 1)) {
    last++;
  }

  const positions: number[] = [];
  for (let i = first; i <= last; i++) {
    positions.push($pos.posAtIndex(i, depth));
  }

  return positions;
}

export function collapsedBlockInDirection(
  view: EditorView,
  direction: "up" | "down",
): number | null {
  return adjacentNodeInDirection(
    view,
    direction,
    (node) => isCollapsed(node) && isBlockCollapsible(node),
  );
}

export function collapsedRunSelection(
  view: EditorView,
  pos: number,
): NodeSelection | TextSelection {
  const { doc } = view.state;
  const positions = collapsedPositionsNear(view, pos);
  const [first] = positions;

  if (positions.length === 1) {
    return NodeSelection.create(doc, first);
  }

  const last = positions[positions.length - 1];
  const lastNode = doc.nodeAt(last);
  return TextSelection.create(doc, first, last + (lastNode?.nodeSize ?? 0));
}

/**
 * The collapsed run a selection currently represents — either a `NodeSelection` on one of
 * its blocks or a `TextSelection` spanning the whole run — or null for any other selection.
 */
export function collapsedRunInfo(
  view: EditorView,
  selection: PMSelection,
): { first: number; runEnd: number } | null {
  const { doc } = view.state;
  const node = doc.nodeAt(selection.from);

  if (!isCollapsed(node) || !isBlockCollapsible(node)) {
    return null;
  }

  const positions = collapsedPositionsNear(view, selection.from);
  const [first] = positions;
  const last = positions[positions.length - 1];
  const runEnd = last + (doc.nodeAt(last)?.nodeSize ?? 0);

  if (selection instanceof NodeSelection) {
    return { first, runEnd };
  }

  return selection.from === first && selection.to === runEnd ? { first, runEnd } : null;
}

export function isCollapsedRunSelection(view: EditorView, selection: PMSelection): boolean {
  return collapsedRunInfo(view, selection) !== null;
}

/**
 * Expand the collapsed block at `pos`. Run-aware: a block that's part of a merged run expands
 * the whole run. With `focus`, lands the caret in the first (now visible) block, or
 * node-selects it for a leaf (image, chart, …)
 */
export function expandBlock(editor: Editor, pos: number, focus = true): boolean {
  const { doc } = editor.state;
  const node = doc.nodeAt(pos);

  if (!isCollapsed(node) || !isBlockCollapsible(node)) {
    return false;
  }

  const positions = collapsedPositionsNear(editor.view, pos);
  const [firstPos] = positions;
  const firstNode = doc.nodeAt(firstPos);

  let chain = editor.chain();
  for (const runPos of positions) {
    chain = chain.setCollapsed(runPos, false);
  }

  if (!focus) {
    return chain.run();
  }

  const expanded = chain
    .command(({ tr, dispatch }) => {
      if (dispatch) {
        let selection: NodeSelection | TextSelection;

        if (firstNode && !firstNode.isLeaf) {
          selection = TextSelection.create(
            tr.doc,
            Selection.near(tr.doc.resolve(firstPos + 1), 1).head,
          );
        } else {
          selection = NodeSelection.create(tr.doc, firstPos);
        }

        tr.setSelection(selection);
      }
      return true;
    })
    .focus()
    .run();

  /**
   * A table drops its cells on collapse and re-mounts them on expand. The focused editor
   * can read the stale node-selection DOM range as an all-cells CellSelection before our
   * caret syncs, so re-focus the editor once the cells have painted.
   */
  if (expanded && positions.some((runPos) => doc.nodeAt(runPos)?.type.name === "table")) {
    requestAnimationFrame(() => {
      if (editor.isDestroyed) {
        return;
      }
      editor.view.focus();
    });
  }

  return expanded;
}

export function expandRunHead(editor: Editor, pos: number, text?: string): boolean {
  const isAtom = editor.state.doc.nodeAt(pos)?.isAtom ?? false;

  return editor
    .chain()
    .setCollapsed(pos, false)
    .command(({ tr, dispatch }) => {
      if (dispatch) {
        if (isAtom) {
          tr.setSelection(NodeSelection.create(tr.doc, pos));
        } else {
          tr.setSelection(Selection.near(tr.doc.resolve(pos + 1), 1));
          if (text) {
            tr.insertText(text);
          }
        }
      }
      return true;
    })
    .run();
}
